import React, { useRef, useState, useEffect } from 'react';
// import fixOrientation from 'fix-orientation';

import _ from 'underscore';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import countryList from 'react-select-country-list';

import { useActions } from 'hooks/useActions';
import * as actions from './actions';
import {
  OuterContainer,
  ContainerHeading,
  CompanyPhotoContainer,
  CompanyPhoto,
  CompanyPhotoInput,
  UploadPhotoLabel,
  FieldsContainer,
  DescriptionQuillContainer,
  ExtraCompanySettingsContainer,
  LabelAndCheckboxContainer,
  Label,
  HideTestLabel,
  FlexContainer,
  HideTestCategoriesContainer,
  ErrorMessage,
  TextInput,
  VATAndIconContainer,
  VATIcon,
  TestsPackageOuterContainer,
  TestsPackageContainer,
  TestsPackageNameAndIconContainer,
  TestsPackageName,
  EditAndDeleteIconContainer,
  TestsListContainer,
  TestName,
  FAIcon,
} from './styles';
import { Button } from 'components/Button/RoundButton';
import { userRoles } from 'constants/users';
import { websiteRegex, phoneNumberRegex, zipRegex } from 'constants/regex';
import { Images } from 'assets/images';
import QuillEditor from 'components/QuillEditor/QuillEditorEmail';
import Dropdown from 'components/CustomizableDropdown/CustomizableDropdown';
import { colors } from 'global/colors';
import { Checkbox } from 'components/Checkbox/Checkbox';
import { checkVATForCountries } from 'functions/checkVATForCountries';
import MultiSelectCheckboxDropdown from 'components/MultiSelectDropdownCheckbox/MultiSelectDropdownCheckbox';
import TestsPackageModal from 'components/Modals/testsPackageModal';
import ConfirmModal from 'components/Modals/confirmModal';

export default React.memo(({ userRole }) => {
  const {
    settings: { company },
    loading,
  } = useSelector((state) => state.SettingsPageReducer);

  const { updateCompany } = useActions(actions);

  const hiddenFileInput = useRef(null);
  const [isHoveredOnLogo, setIsHoveredOnLogo] = useState(false);
  const [formError, setFormError] = useState('');
  const [logo, setLogo] = useState(company?.logo ?? '');
  const [name, setName] = useState(company?.name ?? '');
  const [website, setWebsite] = useState(company?.website ?? '');
  const [phone, setPhone] = useState(company?.phone || '');
  const [country, setCountry] = useState(company?.country || '');
  const [VATInformation, setVATInformation] = useState(company?.VATInformation || {});
  const [city, setCity] = useState(company?.city || '');
  const [address, setAddress] = useState(company?.address || '');
  const [zip, setZip] = useState(company?.zip || '');
  const [description, setDescription] = useState(company?.description || '');
  const [hidePayment, setHidePayment] = useState(company?.hidePayment || false);
  const [limitless, setLimitless] = useState(company?.limitless || false);
  const [showCandidateFeedback, setShowCandidateFeedback] = useState(company?.showCandidateFeedback || false);
  const [isPaymentManagedManually, setIsPaymentManagedManually] = useState(company?.isPaymentManagedManually || false);
  const [anonymizeCandidates, setAnonymizeCandidates] = useState(company?.anonymizeCandidates || false);
  const [reminderEmailEnabled, setReminderEmailEnabled] = useState(company?.reminderEmailEnabled || false);
  const [reminderEmailDays, setReminderEmailDays] = useState(company?.reminderEmailDays || 0);
  const [autoArchiveWeeks, setAutoArchiveWeeks] = useState(company?.autoArchiveWeeks || 0);
  const [codeTestValues, setCodeTestValues] = useState(company?.hidetests?.code?.hiddenTestsValues || []);
  const [generalSkillsTestValues, setGeneralSkillsTestValues] = useState(
    company?.hidetests?.generalSkills?.hiddenTestsValues || []
  );
  const [mcpTestValues, setMcpTestValues] = useState(company?.hidetests?.mcpTest?.hiddenTestsValues || []);
  const [testsPackages, setTestsPackages] = useState(company?.testsPackages || []);
  const [premium, setPremium] = useState(company?.premium ?? {});
  const [showTestsPackageModal, setShowTestsPackageModal] = useState(false);
  const [testPackageModalInfo, setTestPackageModalInfo] = useState({});
  const [countries, setCountries] = useState(countryList().getData());
  const [showDeletePackageConfirmationModal, setShowDeletePackageConfirmationModal] = useState(false);

  const deletePackageIndexRef = useRef(null);
  let codeTestOptions = company.testsAvailable.code;
  let generalSkillsTestOptions = company.testsAvailable['General Skills'];
  let mcpTestOptions = company.testsAvailable['Multiple Choice Programming'];

  if (logo === '/images/placeholder-company.png') setLogo(Images.defaultCompanyLogo);

  useEffect(() => {
    if (company?.VATInformation) setVATInformation(company.VATInformation);
  }, [company?.VATInformation]);

  useEffect(() => {
    if (!company?.country && countries[0].value !== '') {
      let copyOfCountries = [...countries];
      copyOfCountries.unshift({ label: 'Select country', value: '' });
      setCountries(copyOfCountries);
    }
    if (company?.country && company.country.length !== 2) {
      let countryNameCode = countryList().getValue(company.country);
      setCountry(countryNameCode ?? 'SE');
    }
  }, [company.country, countries]);

  const handleLogoClick = () => {
    hiddenFileInput.current.click();
  };

  const setStateOfHiddenTestValues = (category, value) => {
    if (category === 'code') setCodeTestValues(value ? value : codeTestOptions);
    if (category === 'General Skills') setGeneralSkillsTestValues(value ? value : generalSkillsTestOptions);
    if (category === 'Multiple Choice Programming') setMcpTestValues(value ? value : mcpTestOptions);
  };

  const validateForm = () => {
    const maxReminderValue = autoArchiveWeeks * 7 - 1;
    if (!name) setFormError('Company name is required');
    else if (website && !website.match(websiteRegex)) setFormError('Website is not valid');
    else if (phone && !String(phone).match(phoneNumberRegex)) setFormError('Phone number is not valid');
    else if (zip && !zip.match(zipRegex)) setFormError('Zip is not valid');
    else if (reminderEmailEnabled && !(reminderEmailDays >= 1 && reminderEmailDays <= maxReminderValue))
      setFormError(`Set reminder email days between 1 and ${maxReminderValue}`);
    else if (!(autoArchiveWeeks >= 1 && autoArchiveWeeks <= 52))
      setFormError(`Set auto archive weeks between 1 and 52`);
    else {
      setFormError('');
      return true;
    }
  };

  const saveCompany = async () => {
    if (!validateForm()) {
      return;
    }
    // Set hiddenTests values as per the database model
    let hidetests = JSON.parse(JSON.stringify(company.hidetests));

    if (_.keys(codeTestOptions).length === codeTestValues.length) hidetests.code.hidden = true;
    else hidetests.code.hidden = false;
    hidetests.code.hiddenTests = codeTestValues;
    delete hidetests.code.hiddenTestsValues;

    if (_.keys(generalSkillsTestOptions).length === generalSkillsTestValues.length)
      hidetests.generalSkills.hidden = true;
    else hidetests.generalSkills.hidden = false;
    hidetests.generalSkills.hiddenTests = generalSkillsTestValues;
    delete hidetests.generalSkills.hiddenTestsValues;

    if (_.keys(mcpTestOptions).length === mcpTestValues.length) hidetests.mcpTest.hidden = true;
    else hidetests.mcpTest.hidden = false;
    hidetests.mcpTest.hiddenTests = mcpTestValues;
    delete hidetests.mcpTest.hiddenTestsValues;

    updateCompany(company._id, {
      name,
      website,
      phone,
      country,
      VATInformation,
      city,
      address,
      zip,
      description,
      limitless,
      showCandidateFeedback,
      hidePayment,
      isPaymentManagedManually,
      anonymizeCandidates,
      reminderEmailEnabled,
      reminderEmailDays,
      autoArchiveWeeks,
      hidetests,
      logo,
      testsPackages,
      premium,
    });
  };

  const isVATEnabled = () => {
    if (VATInformation?.verification?.status === 'pending') return false;
    if (checkVATForCountries(country)) return true;
    return false;
  };

  const getTestName = (id) => {
    if (id === 'isCandidateChoosesEnabled') return 'Candidate Chooses';
    for (const category in company.testsAvailable) {
      for (const testId in company.testsAvailable[category]) {
        if (testId === id) return company.testsAvailable[category][testId].label;
      }
    }
  };

  const updateTestPackage = (data) => {
    let testsPackagesCopy = JSON.parse(JSON.stringify(testsPackages));
    testsPackagesCopy[data.index] = data;
    delete testsPackagesCopy[data.index].index;
    setTestsPackages(testsPackagesCopy);
    setShowTestsPackageModal(false);
    updateCompany(company._id, { testsPackages: testsPackagesCopy });
  };

  return (
    <OuterContainer>
      <ContainerHeading>Company Settings</ContainerHeading>
      <CompanyPhotoContainer>
        <CompanyPhoto
          image={logo}
          onMouseEnter={() => setIsHoveredOnLogo(true)}
          onMouseLeave={() => setIsHoveredOnLogo(false)}
          onClick={handleLogoClick}
        >
          {isHoveredOnLogo && (
            <UploadPhotoLabel>
              <i className="fa fa-camera mr-1"></i>Upload
            </UploadPhotoLabel>
          )}
        </CompanyPhoto>

        <CompanyPhotoInput
          ref={hiddenFileInput}
          onChange={(e) => {
            let type = e.target.files[0].type;
            if (!['image/png', 'image/jpeg', 'image/jpg', 'image/svg+xml'].includes(type))
              return toast.error('Please upload file of image type');
            const reader = new FileReader();
            reader.readAsDataURL(e.target.files[0]);
            reader.onload = () => {
              setLogo(reader.result);
              // fixOrientation(reader.result, {}, (fixed) => {
              //   setLogo(fixed);
              // });
            };
          }}
        />
      </CompanyPhotoContainer>
      <FieldsContainer>
        <TextInput
          isEditing={true}
          placeholder="Company Name*"
          onChange={(e) => setName(e.target.value)}
          value={name}
        />
        <TextInput
          isEditing={true}
          placeholder="Website"
          onChange={(e) => setWebsite(e.target.value)}
          value={website}
        />
        <TextInput isEditing={true} placeholder="Phone" onChange={(e) => setPhone(e.target.value)} value={phone} />
        <Dropdown
          width="100%"
          value={country}
          margin="9px 0 0 0"
          options={countries}
          onChange={(result) => setCountry(result.value)}
        />
        <VATAndIconContainer>
          <TextInput
            isEditing={isVATEnabled()}
            placeholder="VAT"
            value={VATInformation?.vatId || ''}
            onChange={(e) => setVATInformation((oldValue) => ({ ...oldValue, vatId: e.target.value }))}
            data-toggle="tooltip"
            title={
              VATInformation?.verification?.status === 'verified'
                ? 'VAT is verified'
                : VATInformation?.verification?.status === 'pending'
                  ? 'VAT verification is pending'
                  : VATInformation?.verification?.status
                    ? 'VAT verification is rejected'
                    : 'Not applicable for your country'
            }
          />
          {VATInformation?.vatId && (
            <VATIcon
              color={
                VATInformation?.verification?.status === 'verified'
                  ? colors.fsCyan
                  : VATInformation?.verification?.status === 'pending'
                    ? colors.darkGray
                    : colors.red
              }
              className={
                VATInformation?.verification?.status === 'verified'
                  ? 'fa-regular fa-circle-check'
                  : VATInformation?.verification?.status === 'pending'
                    ? 'fa-regular fa-clock clock'
                    : !company?.VATInformation?.vatId
                      ? ''
                      : 'fa fa-times'
              }
            />
          )}
        </VATAndIconContainer>
        <TextInput isEditing={true} placeholder="City" onChange={(e) => setCity(e.target.value)} value={city} />
        <TextInput
          isEditing={true}
          placeholder="Address"
          onChange={(e) => setAddress(e.target.value)}
          value={address}
        />
        <TextInput
          isEditing={true}
          height="30px"
          placeholder="Postal Code"
          onChange={(e) => setZip(e.target.value)}
          value={zip}
        />
      </FieldsContainer>
      <DescriptionQuillContainer>
        <QuillEditor
          placeholder="Description"
          value={description}
          onChange={(updatedValue) => setDescription(updatedValue)}
        />
      </DescriptionQuillContainer>
      <ExtraCompanySettingsContainer>
        {userRole === userRoles.SUPER_ADMIN && (
          <Checkbox checked={hidePayment} label="Hide Payment Details" onChange={() => setHidePayment(!hidePayment)} />
        )}
        {(userRole === userRoles.SUPER_ADMIN || userRole === userRoles.RESELLER) && (
          <Checkbox checked={limitless} label="Unlimited Customers" onChange={() => setLimitless(!limitless)} />
        )}
        {(userRole === userRoles.SUPER_ADMIN || userRole === userRoles.RESELLER) && (
          <Checkbox
            checked={showCandidateFeedback}
            label="Show candidate feedback to the company"
            onChange={() => setShowCandidateFeedback(!showCandidateFeedback)}
          />
        )}
        {userRole === userRoles.SUPER_ADMIN && (
          <Checkbox
            checked={isPaymentManagedManually}
            label="Manage payment manually"
            onChange={() => setIsPaymentManagedManually(!isPaymentManagedManually)}
          />
        )}
        <Checkbox
          checked={anonymizeCandidates}
          label="Anonymize new candidate data"
          onChange={() => setAnonymizeCandidates(!anonymizeCandidates)}
        />
        <Checkbox
          checked={reminderEmailEnabled}
          label="Send reminder email by default"
          onChange={() => setReminderEmailEnabled(!reminderEmailEnabled)}
        />
        <LabelAndCheckboxContainer>
          <TextInput
            type="number"
            isEditing={true}
            disabled={!reminderEmailEnabled}
            padding="5px 5px 0 5px"
            height="30px"
            width="45px"
            onChange={(e) => setReminderEmailDays(e.target.value)}
            value={reminderEmailDays}
          />
          <Label isInputNumberLabel>Days until reminder email is sent by default</Label>
        </LabelAndCheckboxContainer>
        <LabelAndCheckboxContainer>
          <TextInput
            type="number"
            isEditing={true}
            padding="5px 5px 0 5px"
            height="30px"
            width="45px"
            onChange={(e) => setAutoArchiveWeeks(e.target.value)}
            value={autoArchiveWeeks}
          />
          <Label isInputNumberLabel>Weeks until ignored tests are auto archived and refunded</Label>
        </LabelAndCheckboxContainer>
        {userRole === userRoles.SUPER_ADMIN && (
          <>
            <HideTestLabel>Freecode+ Tests</HideTestLabel>

            <MultiSelectCheckboxDropdown
              options={company.premiumTests}
              usePadding="2px 0 0 25px"
              onSelect={(value) => {
                setPremium({ ...premium, premiumTests: value });
              }}
            />
          </>
        )}
        {userRole === userRoles.SUPER_ADMIN && (
          <>
            <HideTestLabel>Hide Tests</HideTestLabel>
            <HideTestCategoriesContainer>
              <FlexContainer>
                <MultiSelectCheckboxDropdown
                  title="Freecode: "
                  options={codeTestOptions}
                  onSelect={(value) => setStateOfHiddenTestValues('code', value)}
                />
              </FlexContainer>
              <FlexContainer>
                <MultiSelectCheckboxDropdown
                  title="Multichoice: "
                  options={mcpTestOptions}
                  onSelect={(value) => setStateOfHiddenTestValues('Multiple Choice Programming', value)}
                />
              </FlexContainer>
              <FlexContainer>
                <MultiSelectCheckboxDropdown
                  title="General Skills: "
                  options={generalSkillsTestOptions}
                  onSelect={(value) => setStateOfHiddenTestValues('General Skills', value)}
                />
              </FlexContainer>
            </HideTestCategoriesContainer>
          </>
        )}
        <HideTestLabel>Tests Packages:</HideTestLabel>
        <TestsPackageOuterContainer>
          {testsPackages?.map((testsPackage, index) => (
            <TestsPackageContainer key={`package${index}`}>
              <TestsPackageNameAndIconContainer>
                <TestsPackageName>{testsPackage.name}</TestsPackageName>
                <EditAndDeleteIconContainer>
                  <FAIcon
                    icon="fa fa-pencil"
                    marginRight="8"
                    onClick={() => {
                      setTestPackageModalInfo({ ...testsPackage, index });
                      setShowTestsPackageModal(true);
                    }}
                  />
                  <FAIcon
                    icon="fa fa-trash"
                    marginRight="0"
                    onClick={() => {
                      deletePackageIndexRef.current = index;
                      setShowDeletePackageConfirmationModal(true);
                    }}
                  />
                </EditAndDeleteIconContainer>
              </TestsPackageNameAndIconContainer>
              <TestsListContainer>
                {testsPackage.tests.map((test) => (
                  <TestName key={`package${test}`}>- {getTestName(test)}</TestName>
                ))}
              </TestsListContainer>
            </TestsPackageContainer>
          ))}
          <Button
            small
            primaryBlue
            primaryGreenBorder
            margin="auto 5px"
            onClick={() => {
              setTestPackageModalInfo({ name: '', category: 'Package', tests: [], index: testsPackages.length });
              setShowTestsPackageModal(true);
            }}
          >
            <i className="fa fa-plus mr-1"></i> Create
          </Button>
        </TestsPackageOuterContainer>
      </ExtraCompanySettingsContainer>
      {formError && <ErrorMessage>{formError}</ErrorMessage>}
      <Button
        small
        primaryBlue
        primaryGreenBorder
        removeMargin
        disabled={loading}
        margin="10px 0 0 10px"
        onClick={() => saveCompany()}
      >
        Save
      </Button>
      {showTestsPackageModal && (
        <TestsPackageModal
          testsAvailable={company.testsAvailable}
          testPackageModalInfo={testPackageModalInfo}
          updateTestPackage={(data) => updateTestPackage(data)}
          close={() => setShowTestsPackageModal(false)}
        />
      )}
      {showDeletePackageConfirmationModal && (
        <ConfirmModal
          title="Delete package"
          text="Are you sure you want to delete the test package?"
          onCancel={() => setShowDeletePackageConfirmationModal(false)}
          onConfirm={() => {
            let testsPackagesCopy = JSON.parse(JSON.stringify(testsPackages));
            testsPackagesCopy.splice(deletePackageIndexRef.current, 1);
            setTestsPackages(testsPackagesCopy);
            setShowDeletePackageConfirmationModal(false);
            updateCompany(company._id, { testsPackages: testsPackagesCopy });
          }}
        />
      )}
    </OuterContainer>
  );
});
